/**
 * @fileoverview Dashboard API hooks for fetching aggregated statistics
 * Uses dedicated backend endpoints for efficient server-side aggregation
 */
import { useQuery } from '@tanstack/react-query'
import { get } from './client'
import { useSessions, useTokenStats } from './orchestrator'
import { useProjects } from './projects'
import type { DashboardStatsData } from '@/components/dashboard/DashboardStats'
import type { DashboardChartsData } from '@/components/dashboard/DashboardCharts'
import type { RecentActivityData, RecentSession, RecentProject } from '@/components/dashboard/RecentActivityTabs'
import type { SessionMetadata, ProjectSummary } from '@/lib/types'

/**
 * Query key factory for dashboard
 */
export const dashboardKeys = {
  all: ['dashboard'] as const,
  stats: (days: number) => [...dashboardKeys.all, 'stats', days] as const,
  charts: (days: number) => [...dashboardKeys.all, 'charts', days] as const,
  activity: () => [...dashboardKeys.all, 'activity'] as const,
}

// =============================================================================
// Backend Response Types
// =============================================================================

interface StatusCount {
  status: string
  count: number
  color: string
  [key: string]: string | number
}

interface DailyRunCount {
  date: string
  total: number
  successful: number
  failed: number
}

interface DashboardChartsApiResponse {
  runsOverTime: DailyRunCount[]
  statusDistribution: StatusCount[]
  periodDays: number
}

// =============================================================================
// Transform Functions
// =============================================================================

/**
 * Transform sessions to recent activity format
 */
function transformSessions(sessions: SessionMetadata[]): RecentSession[] {
  return sessions.slice(0, 10).map(session => ({
    sessionId: session.sessionId,
    goal: session.lastUserMessage || 'No goal specified',
    status: session.status,
    createdAt: session.createdAt,
    projectId: session.projectId,
    projectName: session.projectName,
  }))
}

/**
 * Transform projects to recent activity format
 */
function transformProjects(projects: ProjectSummary[]): RecentProject[] {
  return projects.slice(0, 10).map(project => ({
    id: project.id,
    name: project.name,
    description: project.description,
    sessionCount: project.sessionCount || 0,
    activeSessionCount: project.activeSessionCount || 0,
    failedSessionCount: project.failedSessionCount || 0,
    createdAt: project.createdAt,
    isArchived: project.isArchived,
  }))
}

// =============================================================================
// API Hooks
// =============================================================================

/**
 * Hook to fetch dashboard charts data from dedicated backend endpoint
 */
function useDashboardChartsApi(days: number = 7) {
  return useQuery({
    queryKey: dashboardKeys.charts(days),
    queryFn: () => get<DashboardChartsApiResponse>(`/dashboard/charts?days=${days}`),
    staleTime: 30 * 1000, // 30 seconds
    refetchInterval: 30 * 1000,
  })
}

/**
 * Combined dashboard data hook
 * Uses dedicated backend endpoints for chart data (efficient aggregation)
 * and existing endpoints for sessions/projects activity
 */
export function useDashboardData(options?: { refetchInterval?: number; days?: number }) {
  const days = options?.days ?? 7
  
  // Fetch chart data from dedicated backend endpoint
  const chartsQuery = useDashboardChartsApi(days)
  
  // Fetch activity data from existing endpoints
  const sessionsQuery = useSessions({ limit: 20, excludeMode: 'Chatbot' })
  const projectsQuery = useProjects({ limit: 20 })
  
  // Fetch token stats from orchestrator (uses all-time data for dashboard overview)
  const tokenStatsQuery = useTokenStats({}, { refetchInterval: options?.refetchInterval })
  
  const isLoading = chartsQuery.isLoading || sessionsQuery.isLoading || projectsQuery.isLoading || tokenStatsQuery.isLoading
  const isFetching = chartsQuery.isFetching || sessionsQuery.isFetching || projectsQuery.isFetching || tokenStatsQuery.isFetching
  const error = chartsQuery.error || sessionsQuery.error || projectsQuery.error || tokenStatsQuery.error
  
  const sessions = sessionsQuery.data?.sessions ?? []
  const projects = projectsQuery.data?.projects ?? []
  const tokenStats = tokenStatsQuery.data
  
  // Transform backend response to frontend format
  const chartsData = chartsQuery.data
  
  // Calculate total tokens from token stats
  const totalTokens = tokenStats?.grandTotal ?? 0
  
  // Calculate stats from chart data
  const stats: DashboardStatsData = {
    activeRuns: chartsData?.statusDistribution?.find(s => s.status === 'Running')?.count ?? 0,
    activeSessions: sessions.filter(s => !s.expiresAt || new Date(s.expiresAt) > new Date()).length,
    totalProjects: projects.filter(p => !p.isArchived).length,
    queuedRuns: chartsData?.statusDistribution?.find(s => s.status === 'Pending')?.count ?? 0,
    failedToday: chartsData?.runsOverTime?.slice(-1)[0]?.failed ?? 0,
    totalTokens,
  }
  
  const charts: DashboardChartsData = {
    runsOverTime: chartsData?.runsOverTime ?? [],
    statusDistribution: chartsData?.statusDistribution ?? [],
  }
  
  const activity: RecentActivityData = {
    sessions: transformSessions(sessions),
    projects: transformProjects(projects),
  }
  
  const refetch = async () => {
    await Promise.all([
      chartsQuery.refetch(),
      sessionsQuery.refetch(),
      projectsQuery.refetch(),
      tokenStatsQuery.refetch(),
    ])
  }
  
  return {
    stats,
    charts,
    activity,
    isLoading,
    isFetching,
    error,
    refetch,
  }
}

/**
 * Hook for just dashboard statistics
 */
export function useDashboardStats() {
  const { stats, isLoading, error } = useDashboardData()
  return { data: stats, isLoading, error }
}

/**
 * Hook for just dashboard charts data
 */
export function useDashboardCharts() {
  const { charts, isLoading, error } = useDashboardData()
  return { data: charts, isLoading, error }
}

/**
 * Hook for just recent activity data
 */
export function useDashboardActivity() {
  const { activity, isLoading, error } = useDashboardData()
  return { data: activity, isLoading, error }
}
